import React, { useState } from 'react';
import { Path, useController } from 'react-hook-form';
import { AlertCircleMIcon } from '@alfalab/icons-glyph/AlertCircleMIcon';
import { CheckmarkOnCircleMIcon } from '@alfalab/icons-glyph/CheckmarkOnCircleMIcon';
import { Badge } from '@alfa-retail-react/components';
import { useFormContext } from '@alfa-retail-react/components/form-hook';

import { UnauthorizedException } from 'app/api/exception/UnauthorizedException';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { ConfirmationPageStore } from 'app/logic/pages/confirmation/ConfirmationPageStore';
import { ValidationRules } from 'app/form/ValidationRules';
import {
    OpenResourceMode,
    DownloadResourceLink,
} from 'app/component/DownloadResourceLink';
import { ResourceFormat } from 'app/model/ResourceFormat';
import { DocumentReference } from 'app/model/DocumentReference';
import { MapBuilder } from 'app/utils/MapBuilder';
import { ConfirmationFormValues } from 'app/logic/pages/confirmation/ConfirmationPage';
import { DocumentCell } from 'app/component/document-cell/DocumentCell';

const mappingStateToIcon = (documentTouched: boolean): React.ReactElement => {
    const successIcon = (
        <Badge
            view="icon"
            iconColor="positive"
            content={<CheckmarkOnCircleMIcon />}
        />
    );
    const errorIcon = (
        <Badge
            view="icon"
            iconColor="negative"
            content={<AlertCircleMIcon />}
        />
    );

    const documentIcon = MapBuilder.create<boolean, React.ReactElement>()
        .value(true, successIcon)
        .value(false, errorIcon)
        .defaultValue(null)
        .build();

    return documentIcon.get(documentTouched);
};

export interface DownloadDocumentFormItemProps {
    document: DocumentReference;

    index: number;

    pageStore: ConfirmationPageStore;

    unauthorizedHandler: (error: UnauthorizedException) => void;
}

/**
 * Компонент отображения документа в списке с функционалом загрузки и предоставления документа пользователю.
 * Переводит поле в валидное состояние по окончанию загрузки документа.
 */
export const DownloadDocumentFormItem = ({
    document,
    index,
    pageStore,
    unauthorizedHandler,
}: DownloadDocumentFormItemProps) => {
    const DOWNLOAD_DOCUMENT_FORM_ITEM_NAME: Path<ConfirmationFormValues> = `documents.${index}.documentTouched`;

    const { control, getValues, setValue, trigger, setFieldElement } =
        useFormContext<ConfirmationFormValues>();
    const [isDownloading, setIsDownloading] = useState(false);

    const { field, fieldState } = useController<ConfirmationFormValues>({
        name: DOWNLOAD_DOCUMENT_FORM_ITEM_NAME,
        control,
        defaultValue: null,
        rules: {
            required: ValidationRules.required('Обязательно к прочтению'),
        },
    });

    return (
        <DownloadResourceLink
            title={document.name}
            className="resourceListItem"
            openResourceMode={OpenResourceMode.NEW_TAB}
            trigger={
                <DocumentCell
                    required
                    ref={(element) => {
                        field.ref(element);
                        setFieldElement(
                            DOWNLOAD_DOCUMENT_FORM_ITEM_NAME,
                            element,
                        );
                    }}
                    addonIcon={mappingStateToIcon(field.value as boolean)}
                    isDownloading={isDownloading}
                    documentName={document.name}
                    error={fieldState.error?.message}
                />
            }
            download={() => {
                return pageStore.actionStateStore.loadData(() => {
                    setIsDownloading(true);

                    return pageStore
                        .getDocumentResource(
                            document.orderId,
                            document.code,
                            ResourceFormat.PDF,
                        )
                        .then((data) => {
                            setIsDownloading(false);
                            setValue(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME, true);
                            trigger(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME);
                            return data;
                        })
                        .catch(
                            exceptionLogger(
                                'DownloadDocumentFormItem',
                                'pageStore.getDocumentResource',
                            ),
                        )
                        .catch((error) => {
                            setIsDownloading(false);
                            
                            if (!getValues(DOWNLOAD_DOCUMENT_FORM_ITEM_NAME)) {
                                setValue(
                                    DOWNLOAD_DOCUMENT_FORM_ITEM_NAME,
                                    false,
                                );
                            }
                            throw error;
                        })
                        .catch((error) => {
                            unauthorizedHandler(error);

                            /**
                             * Отправка ошибки в обработчики DownloadResourceLink
                             */
                            throw error;
                        });
                });
            }}
        />
    );
};
